import React, { useState, useEffect } from 'react'
import { Users, Plus, Shield, Eye, EyeOff, Trash2, Edit, UserCheck, UserX, Crown, User, Key } from 'lucide-react'
import { toast } from 'react-hot-toast'

const UsersTab = ({ onStatsChange }) => {
  const [users, setUsers] = useState({ adminUsers: [], userAccounts: [] })
  const [statistics, setStatistics] = useState({})
  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [activeTab, setActiveTab] = useState('admins') // 'admins' or 'users'
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { user, userType }
  const [passwordReset, setPasswordReset] = useState(null) // { user, userType }
  const [newPassword, setNewPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  
  const [createForm, setCreateForm] = useState({
    userType: 'admin',
    username: '',
    email: '',
    password: '',
    role: 'admin'
  })
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      // Use consolidated token system
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please login to access user management')
        return
      }

      const response = await fetch('/.netlify/functions/admin-list-users-v2', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to fetch users')
      }

      const data = await response.json()
      setUsers(data)
      setStatistics(data.statistics)
    } catch (error) {
      console.error('Fetch users error:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    
    if (!createForm.email || !createForm.password) {
      toast.error('Please fill in all required fields')
      return
    }

    if (createForm.userType === 'admin' && !createForm.username) {
      toast.error('Username is required for admin accounts')
      return
    }

    setLoading(true)
    try {
      // Use consolidated token system
      const token = localStorage.getItem('token')
      
      if (!token) {
        toast.error('Please login first')
        return
      }
      
      const response = await fetch('/.netlify/functions/admin-create-user', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create user')
      }

      const result = await response.json()
      toast.success(result.message)
      
      // Reset form and refresh users
      setCreateForm({
        userType: 'admin',
        username: '',
        email: '',
        password: '',
        role: 'admin'
      })
      setShowCreateForm(false)
      fetchUsers()
      // Refresh admin stats after user creation
      if (onStatsChange) {
        onStatsChange()
      }
      
    } catch (error) {
      console.error('Create user error:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (user, userType) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      
      if (!token) {
        toast.error('Please login first')
        return
      }
      
      const response = await fetch('/.netlify/functions/admin-delete-user', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id,
          userType: userType
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete user')
      }

      const result = await response.json()
      toast.success(result.message)
      
      setDeleteConfirm(null)
      fetchUsers()
      // Refresh admin stats after user deletion
      if (onStatsChange) {
        onStatsChange()
      }
      
    } catch (error) {
      console.error('Delete user error:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (user, userType) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please login first')
        return
      }
      
      const response = await fetch('/.netlify/functions/reset-user-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user.email,
          newPassword: newPassword
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to reset password')
      }

      const result = await response.json()
      toast.success(`Password reset successfully for ${user.email}`)
      
      setPasswordReset(null)
      setNewPassword('')
      setShowNewPassword(false)
      
    } catch (error) {
      console.error('Reset password error:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-yellow-400" />
      case 'uploader': return <Shield className="w-4 h-4 text-blue-400" />
      case 'viewer': return <User className="w-4 h-4 text-green-400" />
      default: return <User className="w-4 h-4 text-gray-400" />
    }
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30'
      case 'uploader': return 'bg-blue-600/20 text-blue-300 border-blue-600/30'
      case 'viewer': return 'bg-green-600/20 text-green-300 border-green-600/30'
      default: return 'bg-gray-600/20 text-gray-300 border-gray-600/30'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Statistics */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Professional User Management</span>
          </h3>
          <p className="text-gray-400 text-sm mt-1">Secure Supabase-backed authentication system</p>
        </div>
        
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={loading}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create User</span>
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center space-x-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-400">Total Admins</p>
                <p className="text-lg font-semibold text-white">{statistics.totalAdmins || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card p-4">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-sm text-gray-400">Total Users</p>
                <p className="text-lg font-semibold text-white">{statistics.totalUsers || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card p-4">
            <div className="flex items-center space-x-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-sm text-gray-400">Active Accounts</p>
                <p className="text-lg font-semibold text-white">{(statistics.activeAdmins || 0) + (statistics.activeUsers || 0)}</p>
              </div>
            </div>
          </div>
          
          <div className="card p-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-netflix-red" />
              <div>
                <p className="text-sm text-gray-400">Total Accounts</p>
                <p className="text-lg font-semibold text-white">{statistics.totalAccounts || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1">
        <button
          onClick={() => setActiveTab('admins')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'admins'
              ? 'bg-netflix-red text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
        >
          <Crown className="w-4 h-4" />
          <span>Admin Users ({users.adminUsers?.length || 0})</span>
        </button>
        
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'users'
              ? 'bg-netflix-red text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Regular Users ({users.userAccounts?.length || 0})</span>
        </button>
      </div>

      {/* Users List */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-netflix-red mx-auto mb-4"></div>
            <p className="text-gray-400">Loading users...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {activeTab === 'admins' ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="p-4 text-gray-300 font-medium">Admin Details</th>
                    <th className="p-4 text-gray-300 font-medium">Role</th>
                    <th className="p-4 text-gray-300 font-medium">Status</th>
                    <th className="p-4 text-gray-300 font-medium">Last Login</th>
                    <th className="p-4 text-gray-300 font-medium">Created</th>
                    <th className="p-4 text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.adminUsers?.map((admin) => (
                    <tr key={admin.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                      <td className="p-4">
                        <div>
                          <div className="text-white font-medium">{admin.username}</div>
                          <div className="text-sm text-gray-400">{admin.email}</div>
                          {admin.created_by && (
                            <div className="text-xs text-gray-500">Created by: {admin.created_by}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(admin.role)}`}>
                          {getRoleIcon(admin.role)}
                          <span className="capitalize">{admin.role}</span>
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                          admin.is_active 
                            ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                            : 'bg-red-600/20 text-red-400 border border-red-600/30'
                        }`}>
                          {admin.is_active ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                          <span>{admin.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      </td>
                      <td className="p-4 text-gray-300 text-sm">{formatDate(admin.last_login)}</td>
                      <td className="p-4 text-gray-300 text-sm">{formatDate(admin.created_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            className="text-gray-400 hover:text-yellow-400 p-1"
                            onClick={() => setPasswordReset({ user: admin, userType: 'admin' })}
                            title="Reset admin password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-gray-400 hover:text-white p-1"
                            title="Edit admin (coming soon)"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-gray-400 hover:text-red-400 p-1"
                            onClick={() => setDeleteConfirm({ user: admin, userType: 'admin' })}
                            title="Delete admin user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="p-4 text-gray-300 font-medium">User Details</th>
                    <th className="p-4 text-gray-300 font-medium">Role</th>
                    <th className="p-4 text-gray-300 font-medium">Invite Code</th>
                    <th className="p-4 text-gray-300 font-medium">Status</th>
                    <th className="p-4 text-gray-300 font-medium">2FA</th>
                    <th className="p-4 text-gray-300 font-medium">Last Login</th>
                    <th className="p-4 text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.userAccounts?.map((user) => (
                    <tr key={user.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                      <td className="p-4">
                        <div>
                          <div className="text-white font-medium">{user.email}</div>
                          <div className="text-xs text-gray-500">ID: {user.id.slice(0, 8)}...</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                          {getRoleIcon(user.role)}
                          <span className="capitalize">{user.role}</span>
                        </span>
                      </td>
                      <td className="p-4">
                        <code className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-300">
                          {user.invite_code || 'None'}
                        </code>
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                          user.is_active 
                            ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                            : 'bg-red-600/20 text-red-400 border border-red-600/30'
                        }`}>
                          {user.is_active ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                          <span>{user.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.requires_2fa 
                            ? 'bg-green-600/20 text-green-400' 
                            : 'bg-gray-600/20 text-gray-400'
                        }`}>
                          {user.requires_2fa ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="p-4 text-gray-300 text-sm">{formatDate(user.last_login)}</td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            className="text-gray-400 hover:text-yellow-400 p-1"
                            onClick={() => setPasswordReset({ user: user, userType: 'user' })}
                            title="Reset user password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-gray-400 hover:text-white p-1"
                            title="Edit user (coming soon)"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-gray-400 hover:text-red-400 p-1"
                            onClick={() => setDeleteConfirm({ user: user, userType: 'user' })}
                            title="Delete user account"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {((activeTab === 'admins' && users.adminUsers?.length === 0) || 
              (activeTab === 'users' && users.userAccounts?.length === 0)) && (
              <div className="p-8 text-center">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No {activeTab === 'admins' ? 'admin users' : 'user accounts'} found</p>
                <p className="text-gray-500 text-sm">Create the first {activeTab === 'admins' ? 'admin' : 'user'} to get started</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h4 className="text-lg font-semibold text-white">Create New User</h4>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              {/* User Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">User Type</label>
                <select
                  value={createForm.userType}
                  onChange={(e) => setCreateForm({ ...createForm, userType: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-netflix-red"
                >
                  <option value="admin">Admin User (Dashboard Access)</option>
                  <option value="user">Regular User (Video Access)</option>
                </select>
              </div>

              {/* Username (Admin only) */}
              {createForm.userType === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-netflix-red"
                    placeholder="Enter admin username"
                    required={createForm.userType === 'admin'}
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-netflix-red"
                  placeholder="Enter email address"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 pr-10 text-white focus:outline-none focus:border-netflix-red"
                    placeholder="Enter secure password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-netflix-red"
                >
                  {createForm.userType === 'admin' ? (
                    <option value="admin">Admin</option>
                  ) : (
                    <>
                      <option value="viewer">Viewer</option>
                      <option value="uploader">Uploader</option>
                      <option value="admin">Admin</option>
                    </>
                  )}
                </select>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-red-600/50 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                <span>Delete {deleteConfirm.userType === 'admin' ? 'Admin User' : 'User Account'}</span>
              </h4>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4 mb-4">
                <p className="text-red-300 font-medium mb-2">⚠️ Permanent Action</p>
                <p className="text-gray-300 text-sm">
                  This will permanently delete the {deleteConfirm.userType === 'admin' ? 'admin user' : 'user account'} and cannot be undone.
                </p>
                {deleteConfirm.userType === 'user' && (
                  <p className="text-gray-400 text-sm mt-2">
                    All videos uploaded by this user will also be deleted.
                  </p>
                )}
              </div>
              
              <div className="bg-gray-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-400">Deleting:</p>
                <p className="text-white font-medium">
                  {deleteConfirm.userType === 'admin' 
                    ? `${deleteConfirm.user.username} (${deleteConfirm.user.email})` 
                    : deleteConfirm.user.email
                  }
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-400 hover:text-white"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(deleteConfirm.user, deleteConfirm.userType)}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                >
                  {loading ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center space-x-2">
                <Key className="w-5 h-5 text-yellow-400" />
                <span className="text-lg font-semibold text-white">Reset Password</span>
              </div>
              <button
                onClick={() => {
                  setPasswordReset(null)
                  setNewPassword('')
                  setShowNewPassword(false)
                }}
                className="ml-auto text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                Reset password for {passwordReset.userType === 'admin' ? 'admin user' : 'user'}:
              </p>
              <p className="text-white font-medium bg-gray-700 p-2 rounded">
                {passwordReset.userType === 'admin' 
                  ? `${passwordReset.user.username} (${passwordReset.user.email})` 
                  : passwordReset.user.email
                }
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Enter new password (min 6 characters)"
                  minLength="6"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Password will be updated immediately</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setPasswordReset(null)
                  setNewPassword('')
                  setShowNewPassword(false)
                }}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResetPassword(passwordReset.user, passwordReset.userType)}
                disabled={loading || !newPassword || newPassword.length < 6}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersTab